import React, { useState, useEffect } from 'react';
import exercisesData from './Data/sentenceGap.json';
import SEO, { SITE_NAME } from '../../Components/SEO';

const SentenceGap = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const currentExercise = exercisesData[currentIndex];

  // Reset state when changing exercise
  useEffect(() => {
    setUserAnswers({});
    setShowResults(false);
  }, [currentIndex]);

  const handleSelect = (gapId, value) => {
    setUserAnswers(prev => ({ ...prev, [gapId]: value }));
  };

  const checkAnswers = () => {
    setShowResults(true);
  };

  const nextExercise = () => {
    if (currentIndex < exercisesData.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevExercise = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  // Helper to render text with dropdowns
  const renderTextWithGaps = (text) => {
    const parts = text.split(/(\[\d+\])/g);
    return parts.map((part, index) => {
      const match = part.match(/\[(\d+)\]/);
      if (match) {
        const gapId = match[1];
        const isCorrect = userAnswers[gapId] === currentExercise.answers[gapId];
        
        return (
          <select
            key={index}
            value={userAnswers[gapId] || ''}
            onChange={(e) => handleSelect(gapId, e.target.value)}
            style={{
              margin: '0 5px',
              padding: '4px',
              borderRadius: '4px',
              border: showResults 
                ? (isCorrect ? '2px solid green' : '2px solid red') 
                : '1px solid #ccc',
              backgroundColor: showResults 
                ? (isCorrect ? '#e6fffa' : '#fff5f5') 
                : 'white'
            }}
          >
            <option value="">({gapId})</option>
            {currentExercise.options.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.id}</option>
            ))}
          </select>
        );
      }
      return part;
    });
  };

  // Logic to determine which options are currently selected in any gap
  const selectedOptionIds = Object.values(userAnswers);

  return (
    <div style={{ maxWidth: '800px', margin: '20px auto', fontFamily: 'sans-serif', lineHeight: '1.6', color: '#333' }}>
      <SEO
        title="Free English Sentence Gap Exercises"
        description="Free English sentence gap-fill exercises. Choose the correct option for each gap and check your answers instantly."
        name={SITE_NAME}
        type="article"
      />
      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <button onClick={prevExercise} disabled={currentIndex === 0}>Previous</button>
        <span>Exercise {currentIndex + 1} of {exercisesData.length}</span>
        <button onClick={nextExercise} disabled={currentIndex === exercisesData.length - 1}>Next</button>
      </div>

      <header style={{ borderBottom: '2px solid #eee', marginBottom: '20px', paddingBottom: '10px' }}>
        <h2>{currentExercise.title}</h2>
        <p style={{ fontStyle: 'italic', color: '#666' }}>{currentExercise.instruction}</p>
      </header>

      {/* Main Text Area */}
      <article style={{ textAlign: 'justify', marginBottom: '30px' }}>
        {currentExercise.paragraphs.map((p, idx) => (
          <p key={idx}>{renderTextWithGaps(p)}</p>
        ))}
      </article>

      {/* Sentence Options */}
      <section style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px' }}>
        <h3>Options:</h3>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {currentExercise.options.map(option => {
            // Check if this specific option is used in any gap
            const isUsed = selectedOptionIds.includes(option.id);
            
            return (
              <li 
                key={option.id} 
                style={{ 
                  marginBottom: '10px', 
                  display: 'flex',
                  // Apply strikethrough and fade effect if used
                  textDecoration: isUsed ? 'line-through' : 'none',
                  color: isUsed ? '#999' : '#333',
                  transition: 'color 0.2s, text-decoration 0.2s'
                }}
              >
                <strong style={{ marginRight: '10px', minWidth: '20px' }}>{option.id}</strong>
                <span>{option.text}</span>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Footer Actions */}
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        <button 
          onClick={checkAnswers}
          style={{ padding: '10px 25px', fontSize: '16px', cursor: 'pointer', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}
        >
          Check Answers
        </button>
        {showResults && (
            <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
                Score: {Object.keys(currentExercise.answers).filter(key => userAnswers[key] === currentExercise.answers[key]).length} / {Object.keys(currentExercise.answers).length}
            </p>
        )}
      </div>
    </div>
  );
};

export default SentenceGap;
